import { SnowAttribute } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { 
	WizardStep, 
	ReviewSectionData, 
	WizardFormData, 
	WizardStepOptions,
	ConditionalWizardStepOptions,
	WizardValidationResult,
	ReviewableValue,
} from '../@types/GenericForm.types';


export function createWizardStep<T extends WizardFormData>(
	title: string,
	attributes: SnowAttribute[],
	options?: WizardStepOptions
): WizardStep<T> {
	return {
		title,
		description: options?.description,
		attributes,
		reviewGroup: options?.reviewGroup || title,
		infoMessage: options?.infoMessage,
	};
}


export function createConditionalWizardStep<T extends WizardFormData>(
	title: string,
	attributes: SnowAttribute[],
	infoMessage: string,
	options: ConditionalWizardStepOptions<T>
): WizardStep<T> {
	return {
		title,
		description: options.description,
		attributes,
		showCondition: options.condition,
		infoMessage,
		reviewGroup: options.reviewGroup || title,
	};
}


export function generateReviewSectionsFromSteps<T extends WizardFormData>(
	steps: WizardStep<T>[],
	formData: T
): ReviewSectionData[] {

	const groupedAttributes: Record<string, SnowAttribute[]> = {};
	
	steps
		.filter(step => !step.showCondition || step.showCondition(formData))
		.forEach(step => {
			const groupName = step.reviewGroup || step.title;

			if (!groupedAttributes[groupName]) {
				groupedAttributes[groupName] = [];
			}

			groupedAttributes[groupName].push(...step.attributes);
		});


	return Object.entries(groupedAttributes).map(([title, attributes]) => ({
		title,
		items: attributes
			.filter(attr => attr.attributeId)
			.map(attr => ({
				label: attr.attributeName || attr.attributeId || 'Unknown Field',
				value: getNestedValue(formData, attr.attributeId || ''),
				formatter: createValueFormatter(),
			})),
	}));
}


function getNestedValue<T extends WizardFormData>(
	obj: T, 
	path: string
): ReviewableValue {
	try {
		return path.split('.').reduce((current: unknown, key: string) => {
			if (current && typeof current === 'object' && current !== null) {
				return (current as WizardFormData)[key];
			}

			return undefined;
		}, obj) as ReviewableValue;
	} catch (error) {
		console.warn(`Failed to get nested value for path: ${path}`, error);

		return undefined;
	}
}


export function validateWizardSteps<T extends WizardFormData>(
	steps: WizardStep<T>[],
	formData: T
): WizardValidationResult {
	const errors: string[] = [];
	const warnings: string[] = [];
	
	steps
		.filter(step => !step.showCondition || step.showCondition(formData))
		.forEach(step => {
			step.attributes
				.filter(attr => attr.mandatory && attr.attributeId)
				.forEach(attr => {
					const value = getNestedValue(formData, attr.attributeId || '');
					const fieldName = attr.attributeName || attr.attributeId || 'Unknown Field';

					if (isValueEmpty(value)) {
						errors.push(`${fieldName} is required`);
					} else if (isValueSuspicious(value)) {
						warnings.push(`${fieldName} may need review`);
					}
				});
		});

	return {
		isValid: errors.length === 0,
		errors,
		warnings,
	};
}


function isValueEmpty(value: ReviewableValue): boolean {
	if (value === undefined || value === null) return true;
	if (typeof value === 'string' && value.trim() === '') return true;
	if (Array.isArray(value) && value.length === 0) return true;
	if (typeof value === 'object' && value !== null && Object.keys(value).length === 0) return true;
	
	return false;
}


function isValueSuspicious(value: ReviewableValue): boolean {
	if (typeof value === 'string') {
		const suspiciousPatterns = [
			/^(test|placeholder|example|todo|fixme)/i,
			/^(xxx|yyy|zzz)/i,
			/^(temp|temporary)/i,
		];

		return suspiciousPatterns.some(pattern => pattern.test(value));
	}
	
	return false;
}


function createValueFormatter(): ((value: ReviewableValue) => string) | undefined {

	return undefined;
}
